import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Product } from '../../../hooks/useProducts';
import { useCart } from '../../../contexts/CartContext';

interface RelatedProductsProps {
  products: Product[];
}

interface RelatedCardProps {
  product: Product;
  onAddToCart: (e: React.MouseEvent, product: Product) => void;
  added: boolean;
}

function RelatedCard({ product, onAddToCart, added }: RelatedCardProps) {
  const allImages = [product.image_url, ...(product.galeri_gorseller || [])].filter(Boolean) as string[];
  const [imgIndex, setImgIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const handleMouseEnter = () => {
    setHovered(true);
    if (allImages.length <= 1) return;
    let idx = 1;
    intervalRef.current = setInterval(() => {
      setImgIndex(idx % allImages.length);
      idx++;
    }, 800);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    setImgIndex(0);
  };

  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : null;

  return (
    <Link
      to={`/urun/${product.slug}`}
      className="group bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative bg-cream overflow-hidden" style={{ aspectRatio: '2/3' }}>
        {allImages.length > 0 ? (
          <img
            src={allImages[imgIndex]}
            alt={product.name}
            className="w-full h-full object-cover object-center transition-opacity duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="ri-image-line text-3xl text-cream-dark" />
          </div>
        )}
        {discount && (
          <span className="absolute top-2.5 left-2.5 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-lg">
            %{discount}
          </span>
        )}
        {allImages.length > 1 && hovered && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
            {allImages.slice(0, 5).map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${i === imgIndex ? 'bg-white scale-125' : 'bg-white/50'}`}
              />
            ))}
          </div>
        )}
        <button
          onClick={(e) => onAddToCart(e, product)}
          className={`absolute bottom-3 right-3 w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 ${
            added ? 'bg-green-500' : 'bg-coffee hover:bg-gold'
          } text-white opacity-0 group-hover:opacity-100 cursor-pointer`}
          aria-label="Sepete ekle"
        >
          <i className={`text-base ${added ? 'ri-check-line' : 'ri-shopping-bag-line'}`} />
        </button>
      </div>
      <div className="p-4">
        {product.category_name && (
          <p className="text-xs text-gold font-medium mb-1">{product.category_name}</p>
        )}
        <h3 className="font-serif text-sm font-semibold text-coffee mb-2.5 group-hover:text-gold transition-colors line-clamp-2 leading-snug">
          {product.name}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-coffee">
            {product.price.toLocaleString('tr-TR')}₺
          </span>
          {product.original_price && (
            <span className="text-xs text-coffee-light line-through">
              {product.original_price.toLocaleString('tr-TR')}₺
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function RelatedProducts({ products }: RelatedProductsProps) {
  const { addItem } = useCart();
  const [addedId, setAddedId] = useState<string | null>(null);

  if (products.length === 0) return null;

  const handleAddToCart = (e: React.MouseEvent, product: Product) => {
    e.preventDefault();
    if (!product.stock || product.stock <= 0) return;
    addItem({
      id: product.id,
      product_id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url,
      slug: product.slug,
      weight: product.weight,
      stock: product.stock,
    });
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1800);
  };

  return (
    <section className="mt-16">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold text-gold uppercase tracking-widest mb-2">Keşfet</p>
          <h2 className="font-serif text-2xl font-bold text-coffee">Benzer Ürünler</h2>
        </div>
        <Link
          to="/urunler"
          className="flex items-center gap-1.5 text-sm font-medium text-coffee-light hover:text-gold transition-colors whitespace-nowrap"
        >
          Tümünü Gör
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line text-sm" />
          </div>
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {products.map(product => (
          <RelatedCard
            key={product.id}
            product={product}
            onAddToCart={handleAddToCart}
            added={addedId === product.id}
          />
        ))}
      </div>
    </section>
  );
}
